import { Text, View, Pressable, RefreshControl, ScrollView } from 'react-native';
import { ActivityIndicator } from 'react-native-paper';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFocusEffect } from 'expo-router';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { useRouter, useNavigation } from 'expo-router';
import { StyleSheet } from 'react-native-unistyles';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import {
    deleteCustomField,
    saveCustomField,
    deleteCustomFieldSection,
    saveCustomFieldSection,
} from '@/data/customFields';
import SearchBox from '@/components/General/inputs/SearchBox';
import EmptyListPlaceholder from '@/components/General/EmptyListPlaceholder';
import { showAlert } from '@/utils/showAlert';
import DropdownMenu, { dropdownIcons } from '@/components/General/DropdownMenu';
import SortableList from '@/components/General/SortableList';
import Unicon from '@/components/General/Unicon';
import useOrganizationEntitlements from '@/utils/hooks/useOrganizationEntitlements';
import { isFreeOrg } from '@/utils/organizationEntitlements';
import PlanRestrictedScreen from '@/components/General/PlanRestrictedScreen';

const FIELD_ICON_MAP = {
    "Text": { name: "text", library: undefined },
    "Long Text": { name: "chatbox", library: undefined },
    "Date": { name: "calendar", library: undefined },
    "Datetime": { name: "time", library: undefined },
    "Number": { name: "sort-numeric-down", library: "fa5" },
    "Checkbox": { name: "toggle", library: undefined },
    "Dropdown": { name: "caret-square-down", library: "fa5" },
    "Multiselect": { name: "list-alt", library: "fa5" },
    "File Upload": { name: "cloud-upload-outline", library: undefined },
};

const reorderIcon = { android: "swap-vertical", ios: "arrow.up.arrow.down", web: "swap-vertical" };
const moveIcon = { android: "swap-horizontal", ios: "rectangle.2.swap", web: "swap-horizontal" };

// ============ FIELD ROW ============
const FieldRow = ({ field, sections, currentSectionID, canEdit, canDelete, onEdit, onDelete, onMove }) => {
    const iconConfig = FIELD_ICON_MAP[field.type] || { name: "help-circle-outline" };
    const optionCount = field.options?.length || 0;

    const menuOptions = useMemo(() => {
        const opts = [];
        if (canEdit) {
            opts.push({
                name: "Edit Field",
                icon: dropdownIcons.edit,
                onSelect: () => onEdit(field),
            });
        }
        const otherSections = (sections || []).filter(s => s.id !== currentSectionID);
        if (canEdit && otherSections.length > 0) {
            otherSections.forEach(s => {
                opts.push({
                    name: `Move to: ${s.name}`,
                    icon: moveIcon,
                    onSelect: () => onMove(field, s),
                });
            });
        }
        if (canDelete && !field.isProtected) {
            opts.push({
                name: "Delete Field",
                icon: dropdownIcons.delete,
                destructive: true,
                onSelect: () => showAlert({
                    title: "Delete Custom Field?",
                    subtitle: `Are you sure you want to delete "${field.name}"? Existing data for this field will be lost.`,
                    isDestructive: true,
                    confirmText: "Delete",
                    onConfirm: () => onDelete(field),
                }),
            });
        }
        return opts;
    }, [field, sections, currentSectionID, canEdit, canDelete, onEdit, onDelete, onMove]);

    return (
        <View testID={"FieldRow-" + field.name} style={styles.fieldRow}>
            <Pressable
                style={styles.fieldPressable}
                onPress={() => canEdit && onEdit(field)}
            >
                <View style={styles.fieldIconContainer}>
                    <Unicon name={iconConfig.name} library={iconConfig.library} size={16} colorVariant="primary" />
                </View>
                <View style={styles.fieldContent}>
                    <Text numberOfLines={1} style={styles.fieldTitle}>{field.name}</Text>
                    <View style={styles.fieldMetaRow}>
                        <View style={styles.typeChip}>
                            <Text style={styles.typeChipText}>{field.type}</Text>
                        </View>
                        {optionCount > 0 && (
                            <Text style={styles.metaText}>{optionCount} option{optionCount !== 1 ? 's' : ''}</Text>
                        )}
                        {field.isProtected && (
                            <View style={styles.protectedChip}>
                                <Unicon name="lock-closed" size={11} colorVariant="textSecondary" />
                            </View>
                        )}
                    </View>
                </View>
            </Pressable>
            {menuOptions.length > 0 && <DropdownMenu testID="FieldRowMenu" options={menuOptions} />}
        </View>
    );
};

// ============ SECTION CARD ============
const SectionCard = ({
    section,
    fields,
    sections,
    canEditSection,
    canDeleteSection,
    canEditField,
    canDeleteField,
    canCreateField,
    onEditSection,
    onDeleteSection,
    onAddField,
    onReorderFields,
    onEditField,
    onDeleteField,
    onMoveField,
}) => {
    const fieldCount = fields?.length || 0;
    const positionValue = section.position ?? 1000;
    const hasPosition = positionValue < 1000;

    const sectionMenu = useMemo(() => {
        const opts = [];
        if (canEditSection) {
            opts.push({
                name: "Edit Section",
                icon: dropdownIcons.edit,
                onSelect: () => onEditSection(section),
            });
        }
        if (canEditField && fieldCount > 1) {
            opts.push({
                name: "Reorder Fields",
                icon: reorderIcon,
                onSelect: () => onReorderFields(section),
            });
        }
        if (canDeleteSection) {
            opts.push({
                name: "Delete Section",
                icon: dropdownIcons.delete,
                destructive: true,
                onSelect: () => showAlert({
                    title: "Delete Section?",
                    subtitle: `Are you sure you want to delete "${section.name}"? Fields in this section will be deleted as well.`,
                    isDestructive: true,
                    confirmText: "Delete",
                    onConfirm: () => onDeleteSection(section),
                }),
            });
        }
        return opts;
    }, [section, fieldCount, canEditSection, canEditField, canDeleteSection, onEditSection, onReorderFields, onDeleteSection]);

    return (
        <View testID={"SectionCard-" + section.name} style={styles.sectionCard}>
            <View style={styles.sectionHeader}>
                <View style={styles.sectionHeaderIcon}>
                    <Unicon name="layers-outline" size={18} colorVariant="primary" />
                </View>
                <View style={styles.sectionHeaderInfo}>
                    <Text numberOfLines={1} style={styles.sectionTitle}>{section.name}</Text>
                    <View style={styles.sectionMetaRow}>
                        <Text style={styles.metaText}>{fieldCount} field{fieldCount !== 1 ? 's' : ''}</Text>
                        <Text style={styles.metaDot}>•</Text>
                        {hasPosition ? (
                            <Text style={styles.metaText}>Position {positionValue + 1}</Text>
                        ) : (
                            <Text style={styles.warningMetaText}>No position set</Text>
                        )}
                    </View>
                </View>
                {sectionMenu.length > 0 && <DropdownMenu testID="SectionCardMenu" options={sectionMenu} />}
            </View>

            <View style={styles.fieldsContainer}>
                {fieldCount === 0 ? (
                    <View style={styles.emptySection}>
                        <Text style={styles.emptySectionText}>No fields in this section yet</Text>
                    </View>
                ) : (
                    fields.map(field => (
                        <FieldRow
                            key={field.id}
                            field={field}
                            sections={sections}
                            currentSectionID={section.id}
                            canEdit={canEditField}
                            canDelete={canDeleteField}
                            onEdit={onEditField}
                            onDelete={onDeleteField}
                            onMove={onMoveField}
                        />
                    ))
                )}

                {canCreateField && (
                    <Pressable
                        testID={"AddFieldButton-" + section.name}
                        style={({ pressed }) => [styles.addFieldButton, pressed && styles.addFieldButtonPressed]}
                        onPress={() => onAddField(section)}
                    >
                        <Unicon name="add-circle-outline" size={16} colorVariant="primary" />
                        <Text style={styles.addFieldText}>Add Field</Text>
                    </Pressable>
                )}
            </View>
        </View>
    );
};

// ============ MAIN SCREEN ============
export default function CustomFieldsScreen() {
    // Plan gate wraps the screen so paid-only custom-field queries/mutations
    // never mount on a deep link under the Free plan.
    const { entitlements } = useOrganizationEntitlements();
    if (isFreeOrg(entitlements)) return <PlanRestrictedScreen />;
    return <CustomFieldsScreenInner />;
}

function CustomFieldsScreenInner() {
    const [selectedTab, setSelectedTab] = useState("People");
    const [searchText, setSearchText] = useState("");
    const [reorderSectionsOpen, setReorderSectionsOpen] = useState(false);
    const [reorderFieldsTarget, setReorderFieldsTarget] = useState(null);

    const queryClient = useQueryClient();
    const navigation = useNavigation();
    const router = useRouter();

    const { data: loggedInUser, isLoading: isLoadingUser } = Query.people.loggedInUser();

    const module = selectedTab === "People" ? "People" : selectedTab === "Events" ? "Events" : "SmallGroupType";
    const { data: sectionsData, isFetching } = Query.customFieldSections.all({
        module,
        options: { enabled: !!module },
    });

    const sections = sectionsData || [];
    const hasEditPermission = loggedInUser?.can?.("Edit Custom Fields")

    if (!isLoadingUser && !hasEditPermission) {
        router.back();
    }

    const refresh = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: QueryKeys.customFieldSections.all("People") });
        queryClient.invalidateQueries({ queryKey: QueryKeys.customFieldSections.all("Events") });
        queryClient.invalidateQueries({ queryKey: QueryKeys.customFieldSections.all("SmallGroupType") });
        queryClient.invalidateQueries({ queryKey: QueryKeys.customFields.all("People") });
        queryClient.invalidateQueries({ queryKey: QueryKeys.customFields.all("Events") });
        queryClient.invalidateQueries({ queryKey: QueryKeys.customFields.all("SmallGroupType") });
    }, [queryClient]);

    // Refresh data when screen comes into focus (e.g., after navigating back from create/edit)
    useFocusEffect(
        useCallback(() => {
            refresh();
        }, [refresh])
    );

    /*** Mutations ***/
    const saveSectionMutation = useMutation({
        mutationFn: (details) => saveCustomFieldSection({ ...details, type: module }),
        onSuccess: refresh,
    });
    const deleteSectionMutation = useMutation({
        mutationFn: (id) => deleteCustomFieldSection(id),
        onSuccess: refresh,
    });
    const saveFieldMutation = useMutation({
        mutationFn: ({ details }) => saveCustomField({ details: { ...details, module } }),
        onSuccess: refresh,
    });
    const deleteFieldMutation = useMutation({
        mutationFn: (id) => deleteCustomField(id),
        onSuccess: refresh,
    });

    /*** Permissions ***/
    const canCreateSection = loggedInUser?.can?.("Create Custom Fields");
    const canEditSection = loggedInUser?.can?.("Edit Custom Fields");
    const canDeleteSection = loggedInUser?.can?.("Delete Custom Fields");
    const canCreateField = canCreateSection;
    const canEditField = canEditSection;
    const canDeleteField = canDeleteSection;

    /*** Header dropdown ***/
    useEffect(() => {
        const opts = [];
        if (canCreateSection) {
            opts.push({
                name: "New Section",
                icon: dropdownIcons.add,
                onSelect: () => router.push({ pathname: "/customfields/sections/create", params: { type: selectedTab } }),
            });
        }
        if (canEditSection && sections.length > 1) {
            opts.push({
                name: "Reorder Sections",
                icon: reorderIcon,
                onSelect: () => setReorderSectionsOpen(true),
            });
        }
        navigation.setOptions({
            headerRight: () => opts.length > 0 ? <DropdownMenu testID="PageMenu" options={opts} /> : null,
        });
    }, [navigation, selectedTab, canCreateSection, canCreateField, canEditSection, sections.length, router]);

    /*** Filtered sections (with nested fields filtered by search) ***/
    const filteredSections = useMemo(() => {
        const q = searchText.trim().toLowerCase();
        return sections.map(s => {
            const allFields = [...(s.CustomFields || [])].sort((a, b) => (a.position ?? 0) - (b.position ?? 0) || a.name.localeCompare(b.name));
            const matchedFields = q
                ? allFields.filter(f => f.name?.toLowerCase().includes(q))
                : allFields;
            const sectionNameMatches = q ? s.name?.toLowerCase().includes(q) : true;

            // when searching, only include the section if its name matches OR it has matching fields
            if (q && !sectionNameMatches && matchedFields.length === 0) return null;

            return { ...s, _fields: q && !sectionNameMatches ? matchedFields : allFields };
        }).filter(Boolean);
    }, [sections, searchText]);

    /*** Handlers ***/
    const onEditSection = useCallback((section) => {
        router.push({ pathname: "/customfields/sections/create", params: { id: section.id, type: selectedTab } });
    }, [router, selectedTab]);

    const onDeleteSection = useCallback((section) => {
        deleteSectionMutation.mutate(section.id);
    }, [deleteSectionMutation]);

    const onAddField = useCallback((section) => {
        router.push({ pathname: "/customfields/create", params: { module, sectionID: section.id } });
    }, [router, module]);

    const onEditField = useCallback((field) => {
        router.push({ pathname: "/customfields/create", params: { id: field.id, module } });
    }, [router, module]);

    const onDeleteField = useCallback((field) => {
        deleteFieldMutation.mutate(field.id);
    }, [deleteFieldMutation]);

    const onMoveField = useCallback((field, targetSection) => {
        saveFieldMutation.mutate({
            details: {
                id: field.id,
                name: field.name,
                type: field.type,
                options: field.options || [],
                sectionID: targetSection.id,
                position: 1000, // place at end of new section; user can reorder
            },
        });
    }, [saveFieldMutation]);

    const onReorderFields = useCallback((section) => {
        setReorderFieldsTarget(section);
    }, []);

    const onConfirmSectionReorder = useCallback((reordered) => {
        Promise.all(
            reordered.map((s, i) =>
                saveSectionMutation.mutateAsync({ ...s, position: i, Fields: s.CustomFields })
            )
        ).then(() => {
            refresh();
        });
    }, [saveSectionMutation, refresh]);

    const onConfirmFieldReorder = useCallback((reordered) => {
        if (!reorderFieldsTarget) return;
        Promise.all(
            reordered.map((f, i) =>
                saveFieldMutation.mutateAsync({
                    details: {
                        id: f.id,
                        name: f.name,
                        type: f.type,
                        options: f.options || [],
                        sectionID: reorderFieldsTarget.id,
                        position: i,
                    },
                })
            )
        ).then(() => {
            refresh();
        });
    }, [saveFieldMutation, reorderFieldsTarget, refresh]);

    const reorderFieldsItems = useMemo(() => {
        if (!reorderFieldsTarget) return [];
        const live = sections.find(s => s.id === reorderFieldsTarget.id);
        const arr = [...((live || reorderFieldsTarget).CustomFields || [])];
        return arr.sort((a, b) => (a.position ?? 0) - (b.position ?? 0) || a.name.localeCompare(b.name));
    }, [reorderFieldsTarget, sections]);

    return (
        <View style={styles.container}>
            <View style={styles.contentWrapper}>
                {/* Tabs */}
                <View style={styles.tabRow}>
                    {["People", "Events", "Small Group Types"].map((tab) => (
                        <Pressable
                            key={tab}
                            testID={"Tab-" + tab}
                            style={styles.tab(selectedTab === tab)}
                            onPress={() => setSelectedTab(tab)}
                        >
                            <Text style={styles.tabText(selectedTab === tab)}>{tab}</Text>
                        </Pressable>
                    ))}
                </View>

                {/* Search */}
                <View style={styles.searchContainer}>
                    <SearchBox
                        testID="CustomFields"
                        searchText={searchText}
                        onChangeText={setSearchText}
                        placeholder={`Search ${selectedTab.toLowerCase()} sections & fields...`}
                    />
                </View>

                {/* List */}
                <ScrollView
                    style={styles.list}
                    contentContainerStyle={styles.listContent}
                    refreshControl={<RefreshControl refreshing={false} onRefresh={refresh} />}
                    showsVerticalScrollIndicator={false}
                    keyboardShouldPersistTaps="handled"
                >
                    {isFetching && sections.length === 0 ? (
                        <View style={styles.loadingContainer}>
                            <ActivityIndicator size="large" />
                        </View>
                    ) : filteredSections.length === 0 ? (
                        <EmptyListPlaceholder
                            message={searchText
                                ? "No sections or fields match your search"
                                : `No ${selectedTab.toLowerCase()} sections yet. Create one from the menu above.`}
                        />
                    ) : (
                        filteredSections.map(section => (
                            <SectionCard
                                key={section.id}
                                section={section}
                                fields={section._fields}
                                sections={sections}
                                canEditSection={canEditSection}
                                canDeleteSection={canDeleteSection}
                                canEditField={canEditField}
                                canDeleteField={canDeleteField}
                                canCreateField={canCreateField}
                                onEditSection={onEditSection}
                                onDeleteSection={onDeleteSection}
                                onAddField={onAddField}
                                onReorderFields={onReorderFields}
                                onEditField={onEditField}
                                onDeleteField={onDeleteField}
                                onMoveField={onMoveField}
                            />
                        ))
                    )}
                    <View style={styles.listFooterSpacer} />
                </ScrollView>
            </View>

            {/* Reorder Sections Modal */}
            <SortableList
                isOpen={reorderSectionsOpen}
                close={() => setReorderSectionsOpen(false)}
                onConfirm={onConfirmSectionReorder}
                items={sections}
                title={`Reorder ${selectedTab} Sections`}
                helpText="Drag sections to set the order they appear on profiles."
                itemContent={(item) => <Text style={styles.sortableText}>{item?.name}</Text>}
                itemHeight={60}
            />

            {/* Reorder Fields Within Section Modal */}
            <SortableList
                isOpen={!!reorderFieldsTarget}
                close={() => setReorderFieldsTarget(null)}
                onConfirm={onConfirmFieldReorder}
                items={reorderFieldsItems}
                title={`Reorder Fields in ${reorderFieldsTarget?.name || ""}`}
                helpText="Drag fields to set the order they appear on profiles."
                itemContent={(item) => <Text style={styles.sortableText}>{item?.name}</Text>}
                itemHeight={60}
            />
        </View>
    );
}

const styles = StyleSheet.create((theme) => ({
    container: { flex: 1 },
    contentWrapper: { flex: 1, maxWidth: 760, width: "100%", alignSelf: "center" },

    // Tabs
    tabRow: {
        flexDirection: "row",
        marginHorizontal: 16,
        marginTop: 12,
        marginBottom: 8,
        backgroundColor: theme.colors.surfaceVariant,
        borderRadius: 10,
        padding: 3,
    },
    tab: (isActive) => ({
        flex: 1,
        paddingVertical: 8,
        borderRadius: 8,
        alignItems: "center",
        backgroundColor: isActive ? theme.colors.surface : "transparent",
        shadowColor: isActive ? "#000" : "transparent",
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: isActive ? 0.08 : 0,
        shadowRadius: 3,
        elevation: isActive ? 2 : 0,
    }),
    tabText: (isActive) => ({
        fontSize: 14,
        fontFamily: isActive ? theme.fonts.bold : theme.fonts.heading,
        color: isActive ? theme.colors.text : theme.colors.textSecondary,
    }),

    // Search
    searchContainer: { paddingHorizontal: 8, paddingBottom: 4, paddingTop: 12 },

    // List
    list: { flex: 1, paddingHorizontal: 8 },
    listContent: { paddingTop: 8, paddingBottom: 30 },
    listFooterSpacer: { paddingBottom: 30 },
    loadingContainer: { padding: 40, alignItems: "center", justifyContent: "center" },

    // Section Card
    sectionCard: {
        backgroundColor: theme.colors.surface,
        borderRadius: 14,
        marginTop: 12,
        borderWidth: 1,
        borderColor: theme.colors.border,
        overflow: "hidden",
    },
    sectionHeader: {
        flexDirection: "row",
        alignItems: "center",
        gap: 12,
        paddingHorizontal: 14,
        paddingVertical: 12,
        backgroundColor: theme.colors.surfaceVariant,
        borderBottomWidth: 1,
        borderBottomColor: theme.colors.border,
    },
    sectionHeaderIcon: {
        width: 36,
        height: 36,
        borderRadius: 10,
        backgroundColor: theme.colors.primaryContainer,
        alignItems: "center",
        justifyContent: "center",
    },
    sectionHeaderInfo: { flex: 1 },
    sectionTitle: {
        fontSize: 16,
        fontFamily: theme.fonts.bold,
        color: theme.colors.text,
    },
    sectionMetaRow: {
        flexDirection: "row",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 6,
        marginTop: 2,
    },
    metaText: {
        fontSize: 12,
        color: theme.colors.textSecondary,
        fontFamily: theme.fonts.body,
    },
    metaDot: {
        fontSize: 12,
        color: theme.colors.textSecondary,
    },
    warningMetaText: {
        fontSize: 12,
        color: theme.colors.warning,
        fontStyle: "italic",
    },

    // Fields
    fieldsContainer: { paddingHorizontal: 8, paddingVertical: 6 },
    fieldRow: {
        flexDirection: "row",
        alignItems: "center",
        paddingHorizontal: 8,
        paddingVertical: 6,
        borderRadius: 10,
        gap: 8,
        _web: {
            transition: "background-color 0.15s ease-in-out",
            _hover: { backgroundColor: theme.colors.hover },
        },
    },
    fieldPressable: {
        flex: 1,
        flexDirection: "row",
        alignItems: "center",
        gap: 10,
    },
    fieldIconContainer: {
        width: 32,
        height: 32,
        borderRadius: 8,
        backgroundColor: theme.colors.primaryContainer,
        alignItems: "center",
        justifyContent: "center",
    },
    fieldContent: { flex: 1 },
    fieldTitle: {
        fontSize: 14,
        fontFamily: theme.fonts.heading,
        color: theme.colors.text,
    },
    fieldMetaRow: {
        flexDirection: "row",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 6,
        marginTop: 2,
    },
    typeChip: {
        backgroundColor: theme.colors.primaryContainer,
        paddingHorizontal: 8,
        paddingVertical: 2,
        borderRadius: 6,
    },
    typeChipText: {
        fontSize: 10,
        fontFamily: theme.fonts.bold,
        color: theme.colors.primary,
    },
    protectedChip: { opacity: 0.6 },

    // Add Field Button
    addFieldButton: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
        marginTop: 4,
        marginHorizontal: 4,
        paddingVertical: 10,
        borderRadius: 10,
        borderWidth: 1,
        borderStyle: "dashed",
        borderColor: theme.colors.border,
        _web: {
            transition: "all 0.15s ease-in-out",
            _hover: {
                backgroundColor: theme.colors.primaryContainer,
                borderColor: theme.colors.primary,
            },
            cursor: "pointer",
        },
    },
    addFieldButtonPressed: {
        backgroundColor: theme.colors.primaryContainer,
        opacity: 0.7,
    },
    addFieldText: {
        fontSize: 13,
        fontFamily: theme.fonts.bold,
        color: theme.colors.primary,
    },

    // Empty section
    emptySection: {
        alignItems: "center",
        paddingVertical: 12,
    },
    emptySectionText: {
        fontSize: 13,
        color: theme.colors.textSecondary,
        fontStyle: "italic",
    },

    // Sortable
    sortableText: {
        color: theme.colors.text,
        fontSize: 15,
        paddingLeft: 10,
        fontFamily: theme.fonts.heading,
    },
}));
